import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { auth, rules } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';
import {
  Button,
  ConsequenceNote,
  Pill,
  SkeletonSet,
  SuccessEmpty,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { mayReadRuleAlerts, usePermissions } from '../permissions.js';
import { gateSentence, refusalGate } from '../authorityGate.js';
import { SettingsCard, SettingsHeader } from './common.js';
import { AutomationBuilder } from './AutomationBuilder.js';
import {
  availableTiers,
  baseRuleType,
  SUSPENDED_AUTHORITY_TEXT,
  TIER_LABEL,
} from './automationTiers.js';
import {
  asNumber,
  categoryLabel,
  describeSchedule,
  paramKind,
  paramLabel,
  scheduleOptions,
  SEV_PILL,
} from './rulesVocabulary.js';
import { LoadFailure } from '../LoadFailure.js';

// Re-exported so the surface's vocabulary keeps one import path for existing
// callers and tests; the implementations live in rulesVocabulary.ts, shared
// with the create builder.
export { categoryLabel, describeCron, paramLabel } from './rulesVocabulary.js';

type Automation = rules.AutomationView;
type CatalogEntry = rules.RuleCatalogEntry;
type RulePatch = rules.RulePatch;
type Rule = rules.RuleView;

/**
 * Business state → pill label/variant. `unavailable` is the engine saying it
 * CANNOT run a switched-on rule (unregistered type or unreadable cadence) —
 * a separate word from Active because "on" and "running" are not the same
 * fact, and the rule that quietly stopped firing is the one case where the
 * conflation costs the most.
 */
const STATE_META: Record<string, { label: string; variant: Severity }> = {
  active: { label: 'Active', variant: 'good' },
  simulating: { label: 'Simulating', variant: 'unsure' },
  paused: { label: 'Paused', variant: 'plain' },
  draft: { label: 'Draft', variant: 'plain' },
  unavailable: { label: 'Can’t run', variant: 'look' },
};

/**
 * THE KEYS THESE PANELS' ROUTES CHARGE, named here so a refusal can say one.
 *
 * `rules:read` is `guard(request, reply, 'rules:read')` on all four history
 * routes (rules/routes.ts:285, :318, :341, :358), and that guard is `can()`
 * with no scope — organization-wide by default. `directory:read` is the SECOND
 * key `GET /rules/:id/alerts` charges (:344 → :167), and it is the one the
 * server names in its own 403 body, so the alerts panel declares that one: it
 * is the key `mayReadRuleAlerts` fires on and the only one of the two that can
 * refuse a caller the `rules:read` half already admitted.
 *
 * A fallback, not an override — `refusalGate` prefers the key the response
 * itself names.
 */
const RULES_READ = 'rules:read';
const DIRECTORY_READ = 'directory:read';

function relTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * Rules & Automations (doc 01 A6.5, doc 13 T2; Track C). A real automation-
 * management surface over the code-defined rule engine:
 *   - List: live rule configs joined with their execution summary (last run,
 *     failures, open alerts) — not decorative rows.
 *   - Create: WHEN (pick a registered trigger template) → IF (tune that rule's
 *     parameters) → THEN (severity + notify/escalate config + the alert it
 *     produces) → schedule → enable. No arbitrary expression builder.
 *   - Edit/detail: execution history, produced alerts, and the audit trail.
 */
export function RulesSettings() {
  const qc = useQueryClient();
  const router = useRouter();
  const { has } = usePermissions();
  const canConfigure = has('rules:configure');
  const canEvaluate = has('rules:evaluate');
  // The tiers THIS caller may create (capability model): personal —
  // automation:manage_own; team — automation:manage_team; organization —
  // rules:configure. A tier the caller cannot create never renders.
  const tiers = availableTiers(has);
  const search = useSearch({ strict: false }) as { rule?: string; create?: string };

  // Same query key as usePermissions, so this dedupes against the permission
  // fetch — the builder needs the acting user's id to namespace a personal or
  // team rule type per owner.
  const me = useQuery({
    queryKey: ['auth', 'me', 'permissions'],
    queryFn: () => auth.me(),
    staleTime: 5 * 60_000,
  });

  const automations = useQuery({
    queryKey: ['rules', 'automations'],
    queryFn: () => rules.automations(),
  });
  const catalog = useQuery({ queryKey: ['rules', 'catalog'], queryFn: () => rules.catalog() });

  const [creating, setCreating] = useState(search.create === '1');

  const patch = useMutation({
    mutationFn: (input: { id: string; body: RulePatch }) => rules.update(input.id, input.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rules'] });
    },
  });

  const evaluate = useMutation({
    mutationFn: (id: string) => rules.evaluate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rules'] });
    },
  });

  const openRule = (id?: string) =>
    void router.navigate({ to: '/settings/rules', search: id ? { rule: id } : {} });

  const selected = search.rule
    ? (automations.data ?? []).find((r) => r.id === search.rule)
    : undefined;
  // Tier-scoped rules store a per-owner namespaced type; the catalog entry —
  // the friendly name, description, and parameter shapes — belongs to the
  // BASE type behind it.
  const selectedCatalogEntry = selected
    ? (catalog.data ?? []).find((c) => c.type === baseRuleType(selected.ruleType))
    : undefined;

  /** Trigger metadata for a row, resolved through the base type when the
   *  server had none for a namespaced tier-scoped type. */
  const triggerCategoryOf = (r: Automation): string | null =>
    r.triggerCategory ??
    (catalog.data ?? []).find((c) => c.type === baseRuleType(r.ruleType))?.triggerCategory ??
    null;

  const columns: Column<Automation>[] = [
    {
      key: 'name',
      header: 'Automation',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.name}</span>
          {r.authorityTier !== 'organization' ? (
            <span style={{ marginLeft: 'var(--space-6)' }}>
              <Pill variant="sky">{TIER_LABEL[r.authorityTier]}</Pill>
            </span>
          ) : null}
          {r.triggerConcept ? (
            <span className="rn-muted" style={{ display: 'block', fontSize: 12 }}>
              {r.triggerConcept}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'trigger',
      header: 'Trigger',
      render: (r) => (
        <Pill variant="sky" dot>
          {categoryLabel(triggerCategoryOf(r))}
        </Pill>
      ),
    },
    {
      key: 'schedule',
      header: 'Schedule',
      render: (r) => <span className="rn-muted">{describeSchedule(r.scheduleConfig)}</span>,
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (r) => (
        <Pill variant={SEV_PILL[r.severity] ?? 'plain'} dot>
          {r.severity}
        </Pill>
      ),
    },
    {
      key: 'state',
      header: 'State',
      render: (r) => {
        // Suspension is the TRUE state and outranks the derived one: the
        // executor found, at fire time, that the owner's present authority no
        // longer covers this automation (model rule 2). Never hidden, never
        // softened into a plain "Paused".
        if (r.suspendedReason === 'suspended_authority') {
          return (
            <Pill variant="look" dot>
              Paused: no authority
            </Pill>
          );
        }
        const meta = STATE_META[r.state] ?? { label: r.state, variant: 'plain' as Severity };
        return (
          <Pill variant={meta.variant} dot>
            {meta.label}
          </Pill>
        );
      },
    },
    {
      key: 'lastActivity',
      header: 'Last activity',
      render: (r) => (
        <span>
          <span
            style={{
              fontWeight: 600,
              color:
                r.execution.lastStatus === 'failed' || r.execution.failedActions > 0
                  ? 'var(--color-urgent-deep)'
                  : undefined,
            }}
          >
            {r.execution.lastStatus ?? 'never'}
          </span>
          {r.execution.lastActivityAt ? (
            <span className="rn-muted" style={{ display: 'block', fontSize: 12 }}>
              {relTime(r.execution.lastActivityAt)}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'failures',
      header: 'Failures',
      render: (r) =>
        r.execution.totalFailed > 0 ? (
          <Pill variant="urgent" dot>
            {r.execution.totalFailed}
          </Pill>
        ) : (
          <span className="rn-muted">—</span>
        ),
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 980 }}>
      <SettingsHeader
        title="Rules & automations"
        blurb="Each automation watches for one thing, then raises an exception when it happens."
      />

      {/* Creation is gated by the capability model's tier keys, not only
          rules:configure: a supervisor holding automation:manage_team (or
          anyone holding automation:manage_own) may create at their tier even
          without the org key. No tier held → no button, absent not grayed. */}
      {tiers.length > 0 ? (
        <div style={{ marginBottom: 'var(--space-12)' }}>
          <button
            type="button"
            className="signin-devbtn"
            style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
            onClick={() => setCreating(true)}
          >
            Create automation
          </button>
        </div>
      ) : null}

      {automations.isLoading ? (
        <SkeletonSet surface="table" rows={5} />
      ) : automations.isError && !automations.data ? (
        <LoadFailure
          error={automations.error}
          message="We couldn’t load automations just now."
          onRetry={() => void automations.refetch()}
        />
      ) : (automations.data ?? []).length === 0 ? (
        <SuccessEmpty
          heading="No automations yet"
          message="Create one to start watching for the things that need attention."
        />
      ) : (
        <TableCard
          columns={columns}
          rows={automations.data ?? []}
          rowKey={(r: Automation) => r.id}
          selectedId={selected?.id}
          onRowOpen={(r: Automation) => openRule(r.id)}
        />
      )}

      {/* `creating && catalog.data` meant that when the catalog fetch failed,
          pressing "Create automation" did nothing at all — no wizard, no
          message, no indication that the button had been heard. The builder
          also needs the acting user's id (to namespace a personal/team rule
          type per owner), so it waits for both fetches the same way. */}
      {creating && catalog.data && me.data ? (
        <AutomationBuilder
          catalog={catalog.data}
          automations={automations.data ?? []}
          tiers={tiers}
          ownerUserId={me.data.userId}
          onDone={(ruleId) => {
            setCreating(false);
            openRule(ruleId);
          }}
          onCancel={() => setCreating(false)}
        />
      ) : creating && (catalog.isError || me.isError) ? (
        <LoadFailure
          // The first of the two that actually failed. Both are reads the
          // builder cannot open without, so either one refusing is the whole
          // answer — and a refusal on either must not offer a retry.
          error={catalog.isError ? catalog.error : me.error}
          message="We couldn’t load what the builder needs, so there is nothing to choose from yet."
          onRetry={() => {
            if (catalog.isError) void catalog.refetch();
            if (me.isError) void me.refetch();
          }}
        />
      ) : creating ? (
        <SkeletonSet surface="drawer" rows={4} />
      ) : null}

      {selected ? (
        <AutomationDetail
          rule={selected}
          catalogEntry={selectedCatalogEntry}
          canConfigure={canConfigure}
          canEvaluate={canEvaluate}
          onClose={() => openRule(undefined)}
          onPatch={(body) => patch.mutate({ id: selected.id, body })}
          onRun={() => evaluate.mutate(selected.id)}
          patching={patch.isPending}
          running={evaluate.isPending}
          runError={evaluate.isError ? 'Evaluation failed — check the execution history.' : null}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Automation detail (edit) — WHEN / IF / THEN + execution + audit
// ---------------------------------------------------------------------------

function AutomationDetail({
  rule,
  catalogEntry,
  canConfigure,
  canEvaluate,
  onClose,
  onPatch,
  onRun,
  patching,
  running,
  runError,
}: {
  rule: Automation;
  catalogEntry?: CatalogEntry;
  canConfigure: boolean;
  canEvaluate: boolean;
  onClose: () => void;
  onPatch: (body: RulePatch) => void;
  onRun: () => void;
  patching: boolean;
  running: boolean;
  runError: string | null;
}) {
  const defaults = catalogEntry?.defaultParams ?? {};
  /**
   * Every condition this rule has, from either side.
   *
   * Catalog order first so the familiar fields stay where an administrator
   * expects them, then anything the rule stores that the catalog does not
   * declare. The second group should normally be empty; when it is not, it is
   * showing a parameter that is set on the record, and hiding it would be the
   * bug this list exists to prevent.
   */
  const conditionKeys = useMemo(
    () => [...new Set([...Object.keys(defaults), ...Object.keys(rule.parameters ?? {})])],
    [defaults, rule.parameters],
  );
  const [draft, setDraft] = useState<RulePatch>(() => ({
    parameters: { ...rule.parameters },
    severity: rule.severity as RulePatch['severity'],
    notificationConfig: { ...rule.notificationConfig },
    escalationConfig: { ...rule.escalationConfig },
    scheduleConfig: { ...rule.scheduleConfig },
  }));

  useEffect(() => {
    setDraft({
      parameters: { ...rule.parameters },
      severity: rule.severity as RulePatch['severity'],
      notificationConfig: { ...rule.notificationConfig },
      escalationConfig: { ...rule.escalationConfig },
      scheduleConfig: { ...rule.scheduleConfig },
    });
  }, [
    rule.id,
    rule.parameters,
    rule.severity,
    rule.notificationConfig,
    rule.escalationConfig,
    rule.scheduleConfig,
  ]);

  const setParam = (key: string, value: unknown) => {
    setDraft((d) => ({
      ...d,
      parameters: { ...(d.parameters ?? {}), [key]: value },
    }));
  };

  const notifyWorker = draft.notificationConfig?.['notifyWorker'] === true;
  const notifySupervisor = draft.notificationConfig?.['notifySupervisor'] === true;
  const escalateEnabled = draft.escalationConfig?.['enabled'] === true;
  const escalationAfterMinutes =
    typeof draft.escalationConfig?.['afterMinutes'] === 'number'
      ? (draft.escalationConfig?.['afterMinutes'] as number)
      : 60;

  return (
    <SettingsCard>
      <div className="rn-card-head">
        <h2 className="rn-card-title">{rule.name}</h2>
        <button type="button" className="rn-link" onClick={onClose}>
          Close
        </button>
      </div>

      {rule.authorityTier !== 'organization' ? (
        <p style={{ marginTop: 0 }}>
          <Pill variant="sky">{TIER_LABEL[rule.authorityTier]}</Pill>{' '}
          <span className="rn-muted" style={{ fontSize: 13 }}>
            {rule.authorityTier === 'personal'
              ? 'Reaches only its owner — that is structural, not a setting.'
              : 'Reaches the workers its owner supervises at the moment it fires, re-checked every run.'}
          </span>
        </p>
      ) : null}

      {/* Suspension honesty (model rule 2): the executor re-checked the
          owner's PRESENT authority at fire time and refused. The true state
          renders here in full — never silently hidden, never a bare "Paused". */}
      {rule.suspendedReason === 'suspended_authority' ? (
        <p role="status" style={{ marginTop: 0, color: 'var(--color-urgent-deep)' }}>
          {SUSPENDED_AUTHORITY_TEXT}
        </p>
      ) : null}

      {/* Engine honesty: switched on but nothing can evaluate it. For a
          personal/team automation this WAS expected until F195 shipped the
          runner; it no longer is, and the tier-specific sentence that said so
          has been removed. Reaching this state now means the base rule type is
          genuinely unregistered, which is the same fault an organization rule
          would report — so both tiers say the same thing, because the cause is
          now the same. Saying so still beats a pill with no sentence behind
          it. */}
      {rule.state === 'unavailable' && rule.suspendedReason === null ? (
        <p role="status" style={{ marginTop: 0, color: 'var(--color-urgent-deep)' }}>
          {
            'This is switched on, but the engine cannot run it — nothing is watching this right now. Your configuration is kept.'
          }
        </p>
      ) : null}

      {(rule.triggerConcept ?? catalogEntry?.triggerConcept) ? (
        <p style={{ marginTop: 0 }}>
          <strong>WHEN</strong> — {rule.triggerConcept ?? catalogEntry?.triggerConcept}
        </p>
      ) : null}
      {catalogEntry?.description ? (
        <p className="rn-muted" style={{ marginTop: 0 }}>
          {catalogEntry.description}
        </p>
      ) : null}

      {canConfigure ? (
        <>
          {/* Turning an automation off is a real change to what the
              organization does — it stops exceptions being raised — and it was
              a text link that looked exactly like the "worker →" navigation
              beside it. Controls, not links. */}
          <div style={{ display: 'flex', gap: 'var(--space-8)', marginBottom: 'var(--space-12)' }}>
            <Button
              variant={rule.enabled ? 'secondary' : 'accent'}
              small
              aria-pressed={rule.enabled}
              onClick={() => onPatch({ enabled: !rule.enabled })}
              disabled={patching}
            >
              {rule.enabled ? 'Turn off' : 'Turn on'}
            </Button>
            <Button
              variant="secondary"
              small
              aria-pressed={rule.simulate}
              onClick={() => onPatch({ simulate: !rule.simulate })}
              disabled={patching}
            >
              {rule.simulate ? 'Test mode: on' : 'Test mode: off'}
            </Button>
            {canEvaluate ? (
              <button
                type="button"
                className="rn-link"
                onClick={() => {
                  // A real (non-test) run produces real exceptions and may send
                  // real notifications — confirm before acting (owner req #5085).
                  if (!rule.simulate) {
                    const ok = window.confirm(
                      'Run this automation for real? It will raise real exceptions in Needs attention and send notifications to the worker and supervisor as configured.',
                    );
                    if (!ok) return;
                  }
                  onRun();
                }}
                disabled={running}
              >
                {running ? 'Running…' : rule.simulate ? 'Test run' : 'Run now'}
              </button>
            ) : null}
          </div>
          <ConsequenceNote text="Run now raises real exceptions and sends notifications. Test mode runs the same check without raising or notifying anyone." />
          {runError ? <p className="rn-muted">{runError}</p> : null}
        </>
      ) : null}

      {/* IF — tenant-configurable conditions (parameters of the code module).

          The UNION of what the catalog declares and what the rule actually
          stores, never just the catalog. This block used to iterate
          `defaults` alone, so a parameter stored on the rule but absent from
          the catalog was invisible: the input showed the catalog's fallback
          while a different key sat in the record, and saving wrote a second
          key alongside the first. A value a person cannot see is a value they
          cannot correct. */}
      {conditionKeys.length > 0 ? (
        <div style={{ marginTop: 'var(--space-16)' }}>
          <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
            IF — conditions
          </span>
          <div style={{ display: 'grid', gap: 'var(--space-10)', marginTop: 'var(--space-8)' }}>
            {conditionKeys.map((key) => {
              // Shape comes from whichever side knows it: the catalog's default
              // when declared, otherwise the stored value itself.
              const def = key in defaults ? defaults[key] : (rule.parameters ?? {})[key];
              const kind = paramKind(def);
              const current = (draft.parameters ?? {})[key];
              return (
                <label key={key} style={{ display: 'block' }}>
                  <span style={{ fontWeight: 600 }}>{paramLabel(key)}</span>
                  {kind === 'number' ? (
                    <input
                      type="number"

                      value={current === undefined ? asNumber(def) : (current as number)}
                      onChange={(e) => setParam(key, Number(e.target.value))}
                      disabled={!canConfigure}
                    />
                  ) : (
                    <input
                      type="text"

                      value={current === undefined ? String(def) : String(current)}
                      onChange={(e) => setParam(key, e.target.value)}
                      disabled={!canConfigure}
                    />
                  )}
                </label>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* THEN — actions */}
      <div style={{ marginTop: 'var(--space-16)' }}>
        <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
          THEN — actions
        </span>
        <div style={{ display: 'grid', gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
          <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={notifyWorker}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  notificationConfig: {
                    ...(d.notificationConfig ?? {}),
                    notifyWorker: e.target.checked,
                  },
                }))
              }
              disabled={!canConfigure}
            />
            Notify worker
          </label>
          <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={notifySupervisor}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  notificationConfig: {
                    ...(d.notificationConfig ?? {}),
                    notifySupervisor: e.target.checked,
                  },
                }))
              }
              disabled={!canConfigure}
            />
            Notify supervisor
          </label>
          <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={escalateEnabled}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  escalationConfig: { ...(d.escalationConfig ?? {}), enabled: e.target.checked },
                }))
              }
              disabled={!canConfigure}
            />
            Escalate if it stays unresolved
          </label>
          {escalateEnabled ? (
            <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Escalate after</span>
              <input
                type="number"
                min={1}

                style={{ width: 96 }}
                value={escalationAfterMinutes}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    escalationConfig: {
                      ...(d.escalationConfig ?? {}),
                      afterMinutes: Math.max(1, Math.floor(Number(e.target.value) || 1)),
                    },
                  }))
                }
                disabled={!canConfigure}
              />
              <span className="rn-muted">minutes</span>
            </label>
          ) : null}
          <span className="rn-muted" style={{ fontSize: 12 }}>
            Notifications fire when a new exception is raised. Escalation fires after the configured
            period if it stays unresolved.
          </span>
          {(rule.actions.notifyWorker ||
            rule.actions.notifySupervisor ||
            rule.actions.escalationEnabled) && (
            <span className="rn-muted" style={{ fontSize: 12 }}>
              {[
                rule.actions.notifyWorker ? 'notify the worker' : null,
                rule.actions.notifySupervisor ? 'notify a supervisor' : null,
                rule.actions.escalationEnabled
                  ? `escalate after ${rule.actions.escalationAfterMinutes ?? 60}m`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
          {rule.execution.failedActions > 0 ? (
            <p className="rn-muted" style={{ fontSize: 12, color: 'var(--color-urgent-deep)' }}>
              Some notifications couldn't be delivered — see &ldquo;Notifications &amp;
              escalation&rdquo; below.
            </p>
          ) : null}
        </div>

        <div style={{ marginTop: 'var(--space-12)' }}>
          <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
            Severity
          </span>
          <div style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
            {(['info', 'warning', 'critical'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className="rn-link"
                aria-pressed={draft.severity === s}
                onClick={() => setDraft((d) => ({ ...d, severity: s }))}
                disabled={!canConfigure}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 'var(--space-12)' }}>
          <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
            Schedule
          </span>
          <div style={{ marginTop: 'var(--space-8)' }}>
            <select
              value={JSON.stringify(draft.scheduleConfig ?? {})}
              onChange={(e) =>
                setDraft((d) => ({ ...d, scheduleConfig: JSON.parse(e.target.value) }))
              }
              disabled={!canConfigure}
            >
              {scheduleOptions(draft.scheduleConfig ?? {}).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {canConfigure ? (
          <div style={{ marginTop: 'var(--space-16)' }}>
            <button
              type="button"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
              onClick={() => onPatch(draft)}
              disabled={patching}
            >
              {patching ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        ) : null}
      </div>

      <EscalationSetting
        key={rule.id}
        rule={rule}
        canConfigure={canConfigure}
        onSave={(escalationConfig) => onPatch({ escalationConfig })}
      />

      <EvaluationHistory ruleId={rule.id} />
      <ActionExecutions ruleId={rule.id} />
      <ProducedAlerts ruleId={rule.id} />
      <AuditHistory ruleId={rule.id} />
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Execution history + produced alerts + audit
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Escalation target setting (owner #5067) — afterMinutes + escalateTo
// ---------------------------------------------------------------------------

function EscalationSetting({
  rule,
  canConfigure,
  onSave,
}: {
  rule: Rule;
  canConfigure: boolean;
  onSave: (config: rules.EscalationConfig) => void;
}) {
  const [minutes, setMinutes] = useState(rule.escalation.afterMinutes?.toString() ?? '');
  const [target, setTarget] = useState<rules.EscalationTarget | ''>(
    rule.escalation.escalateTo ?? '',
  );

  const parsedMinutes = minutes.trim() === '' ? null : Number(minutes);
  const validMinutes =
    parsedMinutes === null || (Number.isInteger(parsedMinutes) && parsedMinutes >= 1);
  const dirty =
    parsedMinutes !== (rule.escalation.afterMinutes ?? null) ||
    (target || null) !== (rule.escalation.escalateTo ?? null);

  const sourceLabel =
    rule.escalation.source === 'rule'
      ? 'rule override'
      : rule.escalation.source === 'org_default'
        ? 'organization default'
        : 'not configured';

  return (
    <div style={{ marginTop: 'var(--space-12)' }}>
      <span className="rn-muted">Escalation</span>
      <p style={{ margin: 'var(--space-6) 0', color: 'var(--color-ink2)' }}>
        If still unresolved after{' '}
        <input
          type="number"
          min={1}
          step={1}
          value={minutes}
          disabled={!canConfigure}
          onChange={(e) => setMinutes(e.target.value)}
          style={{ width: 64, marginRight: 'var(--space-4)' }}
        />{' '}
        minutes, escalate to{' '}
        <select
          value={target}
          disabled={!canConfigure}
          onChange={(e) => setTarget(e.target.value as rules.EscalationTarget | '')}
        >
          <option value="">— choose —</option>
          <option value="supervisor">assigned supervisor</option>
          <option value="program_supervisors">program supervisors</option>
        </select>
      </p>
      <p className="rn-muted" style={{ marginTop: 0, fontSize: 12 }}>
        Currently: {rule.escalation.afterMinutes ?? '—'} min → {rule.escalation.escalateTo ?? '—'} (
        {sourceLabel}).
      </p>
      {canConfigure ? (
        <button
          type="button"
          className="rn-link"
          disabled={!validMinutes || !dirty}
          onClick={() =>
            onSave({
              afterMinutes: parsedMinutes ?? undefined,
              escalateTo: target || undefined,
            })
          }
        >
          Save escalation
        </button>
      ) : null}
    </div>
  );
}

function EvaluationHistory({ ruleId }: { ruleId: string }) {
  const evals = useQuery({
    queryKey: ['rules', 'evaluations', ruleId],
    queryFn: () => rules.evaluations({ ruleId, limit: 10 }),
  });

  if (evals.isLoading) return <SkeletonSet surface="table" rows={2} />;
  if (evals.data === undefined)
    return (
      <HistoryUnavailable what="this rule’s executions" permission={RULES_READ} query={evals} />
    );
  const rows = evals.data;
  if (rows.length === 0) {
    return (
      <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
        No executions yet.
      </p>
    );
  }
  return (
    <div style={{ marginTop: 'var(--space-16)' }}>
      <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
        Execution history
      </span>
      <ul style={{ margin: 'var(--space-6) 0 0', paddingLeft: 'var(--space-16)' }}>
        {rows.map((e) => (
          <li key={e.id} style={{ marginBottom: 'var(--space-6)' }}>
            {new Date(e.startedAt).toLocaleString()} —{' '}
            <span style={{ fontWeight: e.status === 'failed' ? 600 : 400 }}>{e.status}</span>
            {e.status === 'completed' ? ` · ${e.alertsGenerated ?? 0} raised` : ''}
            {e.status === 'failed' ? ' · see logs' : ''}
            {e.simulated ? ' (simulated)' : ''}
            {e.triggeredBy === 'manual' ? ' · manual' : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action history (business outcomes — owner requirement #5085)
// ---------------------------------------------------------------------------
//
// Renders only the customer-facing business outcome for each action. The API
// never returns internal failure states, SQL errors, retry counts, or delivery
// targets here — those stay audit-only.

function ActionExecutions({ ruleId }: { ruleId: string }) {
  const executions = useQuery({
    queryKey: ['rules', 'executions', ruleId],
    queryFn: () => rules.executions(ruleId),
  });

  if (executions.isLoading) return <SkeletonSet surface="table" rows={2} />;
  if (executions.data === undefined)
    return (
      <HistoryUnavailable what="the action history" permission={RULES_READ} query={executions} />
    );
  const rows = executions.data;
  if (rows.length === 0) {
    return (
      <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
        No actions have run yet.
      </p>
    );
  }
  return (
    <div style={{ marginTop: 'var(--space-16)' }}>
      <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
        Action history
      </span>
      <ul style={{ margin: 'var(--space-6) 0 0', paddingLeft: 'var(--space-16)' }}>
        {rows.map((e) => (
          <li key={e.id} style={{ marginBottom: 'var(--space-6)' }}>
            {new Date(e.occurredAt).toLocaleString()} — {e.action}: {e.outcome}
            {e.needsAttention ? (
              <Pill variant="look" dot>
                Needs attention
              </Pill>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProducedAlerts({ ruleId }: { ruleId: string }) {
  /**
   * TWO keys, not one. `GET /rules/:id/alerts` adds `requireDirectoryRead` on
   * top of the `rules:read` guard (rules/routes.ts:291) because, as its 403
   * says, "a rule's alerts name the worker they are about". This panel asked
   * for neither, so a `rules:read` holder without `directory:read` got "We
   * couldn't read the exceptions this rule raised … try again" — a retry
   * offered on a settled answer, beside a rule that may well have raised
   * plenty.
   */
  const permissions = usePermissions();
  const { isLoading: permissionsLoading, isError: permissionsError } = permissions;
  /**
   * The SCOPE-AWARE question, because both of this route's gates ask it.
   * `guard(..., 'rules:read')` is `can()` with no scope — organization-wide by
   * default — and `requireDirectoryRead` is `can(principal.authz,
   * 'directory:read')`, whose comment is "`can()` at organization scope, never
   * a membership test on a flattened key list". Reading the flattened list here
   * promised the panel to a program-scoped holder the route had already decided
   * to refuse.
   */
  const mayRead = mayReadRuleAlerts(permissions);
  const alerts = useQuery({
    queryKey: ['rules', 'alerts', ruleId],
    queryFn: () => rules.alerts(ruleId),
    enabled: mayRead,
  });

  if (permissionsLoading) return <SkeletonSet surface="table" rows={2} />;
  if (!permissionsError && !mayRead) {
    return (
      <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }} role="status">
        The exceptions this rule raised are not shown to you. They name the worker they are about,
        so reading them needs permission to view the workforce directory as well. This is not a rule
        that has raised none.
      </p>
    );
  }
  if (alerts.isLoading) return <SkeletonSet surface="table" rows={2} />;
  if (alerts.data === undefined)
    return (
      <HistoryUnavailable
        what="the exceptions this rule raised"
        permission={DIRECTORY_READ}
        query={alerts}
      />
    );
  const rows = alerts.data;
  if (rows.length === 0) {
    return (
      <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
        No exceptions raised yet.
      </p>
    );
  }
  return (
    <div style={{ marginTop: 'var(--space-16)' }}>
      <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
        Exceptions raised
      </span>
      <ul style={{ margin: 'var(--space-6) 0 0', paddingLeft: 'var(--space-16)' }}>
        {rows.map((a) => (
          <li key={a.id} style={{ marginBottom: 'var(--space-6)' }}>
            <Pill variant={SEV_PILL[a.severity] ?? 'plain'} dot>
              {a.severity}
            </Pill>{' '}
            {a.title} <span className="rn-muted">· {a.status}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AuditHistory({ ruleId }: { ruleId: string }) {
  const audit = useQuery({
    queryKey: ['rules', 'audit', ruleId],
    queryFn: () => rules.audit(ruleId),
  });

  if (audit.isLoading) return <SkeletonSet surface="table" rows={2} />;
  if (audit.data === undefined)
    return (
      <HistoryUnavailable what="this rule’s change history" permission={RULES_READ} query={audit} />
    );
  const rows = audit.data;
  if (rows.length === 0) {
    return (
      <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
        No changes recorded yet.
      </p>
    );
  }
  return (
    <div style={{ marginTop: 'var(--space-16)' }}>
      <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
        Change history
      </span>
      <ul style={{ margin: 'var(--space-6) 0 0', paddingLeft: 'var(--space-16)' }}>
        {rows.map((a) => (
          <li key={a.id} style={{ marginBottom: 'var(--space-6)' }}>
            {new Date(a.occurredAt).toLocaleString()} — {a.action}
            {a.actorUserId ? ` · by ${a.actorUserId.slice(0, 8)}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The four history panels below a rule — executions, actions, exceptions,
 * changes — all read `q.data ?? []` and then printed "No executions yet." for
 * an empty array. So a failed request said the rule had never run, on the
 * exact surface an operator opens to find out whether it did.
 *
 * They are small panels inside a drawer, so this is a line rather than a full
 * `ErrorPanel`; what matters is that it is a different line.
 *
 * ---------------------------------------------------------------------------
 * AND THEN THE SAME COLLAPSE ONE STEP IN: A 403 IS NOT A FAULT
 * ---------------------------------------------------------------------------
 * Splitting *absent* from *unreadable* left *unreadable* holding two states
 * that want opposite words. `query.isError` is true for a dropped connection
 * and for a 403, and this component said the same thing about both — "We
 * couldn't read the action history … try again" — so a reader the server has
 * DECIDED about was told the system had failed and invited to keep pressing a
 * button that returns the identical 403 every time. It never named a key, so
 * nobody could act on it either.
 *
 * All four panels are behind guards that can refuse a caller the route
 * `/settings/rules` already let through: `RequirePermission` gates the route on
 * `has('rules:read')`, which reads the FLATTENED key list, while every one of
 * these four routes is `can()` at ORGANIZATION scope. A program-scoped
 * `rules:read` is inside the screen and refused at the door. The alerts route
 * charges `directory:read` as well, and names it — `mayReadRuleAlerts` fires
 * ahead of the request for that one, and this is what catches the rest,
 * including the alerts refusal that arrives anyway when the two disagree.
 *
 * FOUR STATES, and each one gets its own word:
 *
 *   pending      the read is in flight        `Still reading …`
 *   refused      403 — a settled answer       names the key, NO retry
 *   unreadable   any other failure            says so, and offers the retry
 *   (absent)     answered, and empty          the caller's "No executions yet."
 *
 * `data-history` carries the state onto the DOM so a test can tell them apart
 * without matching prose — the device `GateNotice` uses for the same reason.
 */
function HistoryUnavailable({
  what,
  permission,
  query,
}: {
  /** A noun phrase completing "read …": "the action history". */
  what: string;
  /**
   * The key this panel's ROUTE charges, as the client knows it. Used only when
   * the refusal does not name its own — see `refusalGate`.
   */
  permission: string;
  query: { isError: boolean; error: unknown; refetch: () => unknown };
}) {
  const refused = query.isError ? refusalGate(query.error, permission) : null;
  if (refused) {
    return (
      <p
        className="rn-muted"
        style={{ marginTop: 'var(--space-12)' }}
        role="status"
        data-history="refused"
      >
        {gateSentence(refused, `read ${what}`)} This is not the same as it never having run.
      </p>
    );
  }
  return (
    <p
      className="rn-muted"
      style={{ marginTop: 'var(--space-12)' }}
      role="status"
      data-history={query.isError ? 'unreadable' : 'pending'}
    >
      {query.isError
        ? `We couldn’t read ${what}. This is not the same as it never having run — `
        : `Still reading ${what}…`}
      {query.isError ? (
        <button type="button" className="rn-link" onClick={() => void query.refetch()}>
          try again
        </button>
      ) : null}
    </p>
  );
}
